import chainsig from './gaqlchainsig.js'

import {
  gaqlenumTYPE as TYPE,
  gaqlenumLAZY as LAZY,
  gaqlenumMODE as MODE,
  gaqlenumMODERETURN as MODERETURN,
  gaqlenumERROR as ERROR,
  gaqlenumARGSIG as ARGSIG,
  gaqlenumQueryNameDEFAULT as DEFAULT,
  gaqlenumQueryNameGETFIELD as GETFIELD,
  gaqlenumQueryNameROW as ROW
} from './gaqlenum.js'

import {
  gaqlutilNumBoolOrUndefIs as utilNumBoolOrUndefIs,
  gaqlutilPlainObjIs as utilPlainObjIs
} from './gaqlutil.js'

import {
  gaqltargetArgsIs as targetArgsIs,
  gaqltargetArgsParse as targetArgsParse
} from './gaqltarget.js'

import {
  gaqlerrQueryCannotUseNestedRow as errQueryCannotUseNestedRow,
  gaqlerrQueryInvalidReturn as errQueryInvalidReturn,
  gaqlerrQueryNotFound as errQueryNotFound
} from './gaqlerr.js'

import {
  gaqlchainIs as chainIs,
  gaqlchainIsSub as chainIsSub,
  gaqlchainIsShallow as chainIsShallow,
  gaqlchainIdGet as chainIdGet,
  gaqlchainRecsGet as chainRecsGet
} from './gaqlchain.js'

const runqstIs = qst => (
  utilPlainObjIs(qst) && 'target' in qst)

// if special shorthand 'asfn' it is used, else 'getField' is used
// ex,
//   `q.query('name')('arg-for-getField-or-special-fn').run()`
const queryFnGet = (queryFns, queryName) => {
  const queryNameAsFn = queryName.endsWith('.asfn')
  const queryNameRoot = queryNameAsFn ? queryName.slice(0, -5) : queryName
  const queryFnRoot = queryFns['$' + queryNameRoot]
  const queryFn = (queryFnRoot && queryNameAsFn)
    ? (queryFnRoot.asfn || queryFns['$' + GETFIELD])
    : queryFnRoot

  // TODO some errors such as below should surface outside of 'default'
  if (typeof queryFn !== 'function') {
    console.error(`queryName "${queryName}" not found`)
    throw errQueryNotFound(queryName, queryFns)
  }

  return queryFn
}

// e-slint-disable-next-line max-len
const runRecsNext = async (gst, chain, run, rows, recs, qstNext, i = 0) => {
  if (i >= recs.length)
    return qstNext

  // avoid mutating original args w/ suspended values
  const rec = recs[i]
  const queryArgs = (rec[1] || []).slice()
  const queryName = rec[0]

  if (qstNext.error && DEFAULT !== queryName)
    return runRecsNext(gst, chain, run, rows, recs, qstNext, i + 1)

  if (queryName === ROW) {
    // following authentic rethinkdb, disallow most nested short-hand
    // row queries. legacy 'rethinkdb' driver is sometimes more permissive
    // than newer rethinkdb-ts: rethinkdb-ts behaviour preferred here
    //
    // ex, nested q.row('membership') elicits an error
    // ```
    // q.expr(list).filter( // throws error
    //   q.row('user_id').eq('xavier').or(q.row('membership').eq('join'))
    // ```
    // existance of ARGSIG indicates row function was used
    if (qstNext.rowDepth >= 1 && i === 0 && ARGSIG !== queryArgs[0]) {
      throw errQueryCannotUseNestedRow()
    } else {
      qstNext.rowDepth += 1
    }
  }

  if (i === 0 && rows && !chainIsSub(chain)) {
    // assigns row from callee to this pattern target,
    //  * this pattern must represent the beginning of a chain
    //  * this pattern is not a 'function'; pattern will not resolve row
    //
    // ex, filter passes each item to the embedded 'row'
    // ```
    // q.expr(list).filter(
    //   q.row('time_expire').during(
    //     q.epochTime(0),
    //     q.epochTime(now / 1000)))
    // ```
    qstNext.target = rows[0]
  }

  try {
    // exhaustive params list provides access to full range of behaviours,
    // potentially needed by any query, including external plugin queries
    //
    // param order is determined by each param's importance and anticipated
    // frequency-of-use. first three params commonly needed by any query.
    // remaining params rarely needed.
    //
    // 'run': needed by plugin queries to process subquery args with run()
    // 'reqlObj': current local query data incl. a query recs list and recId
    // 'i': corresponds to query's position at 'reqlObj.recs'
    qstNext = await queryFnGet(gst.queries, queryName)(
      gst, qstNext, queryArgs, run, chain, i)
  } catch (e) {
    // do not throw error if chain subsequently uses `.default(...)`
    // * if no future default query exists, tag error
    // * throw all tagged errors up to user
    qstNext.target = null
    qstNext.error = e

    e[ERROR] = typeof e[ERROR] === 'boolean'
      ? e[ERROR]
      : !chainRecsGet(chain).slice(i + 1).some(o => DEFAULT === o[0])

    if (e[ERROR]) {
      // TODO does not handle design node queryable properly
      // only attach one query sig, one closest to error
      if (!/\n> /.test(e.message))
        e.message = e.message + '\n> ' + chainsig(chain)

      throw e
    }
  }

  if (!runqstIs(qstNext))
    throw errQueryInvalidReturn(queryName)

  if (qstNext && qstNext[MODE] === MODERETURN)
    return qstNext

  return runRecsNext(gst, chain, run, rows, recs, qstNext, i + 1)
}

const gaqlrunChain = async (gst, qst, chain, run, rows) => {
  const chainId = chainIdGet(chain)
  const chainRecs = chainRecsGet(chain)

  if (rows && rows.length) {
    // NOTE mutating this shared 'rowMap' prevents 'map' and 'forEach'
    // from resolving concurrently; each iteration must process its rowMap
    // fully before the next iteration comes along and mutates it.
    //
    // Cloning map like structures is slow in javascript and building an
    // immutable map would require a lot of scripting or big dependency
    //
    // `rowMap` size is expected to remain small. Because of this, `rowMap`
    // items could be mapped to array positions and each iteration receive
    // its own array not shared by the other operations.
    qst.rowMap[chainId] = rows.slice()
  }

  // to avoid browser 'too much recursion' error, queueMicrotask is used, which
  // requires callback usage
  qst = await runRecsNext(gst, chain, run, rows, chainRecs, {
    // if nested spec is not a function expression,
    // pass target value down from parent
    //
    // q.expr(...).map(
    //   q.branch(
    //     q.row('victories').gt(100),
    //     q.row('name').add(' is a hero'),
    //     q.row('name').add(' is very nice')))
    simnode: qst.simnode,
    target: chainRecs[0][0] === ROW ? qst.target : null,
    recId: chainId,
    rowMap: qst.rowMap || {},
    rowDepth: qst.rowDepth || 0
  })

  return qst.target
}

// TODO convert to callback,
//  move callback param lower arg position
const runShallow = async (gst, qst, qspec, rows, d, run) => {
  const qspecr = Array.isArray(qspec) ? [] : {}

  for (let c in qspec) {
    qspecr[c] = await run(gst, qst, qspec[c], rows, d)
  }

  return qspecr
}

const runcb = async (
  gst, qst, qspec, rows, d = 0, type = typeof qspec, f = null
) => {
  if (qspec === f
    || utilNumBoolOrUndefIs(type)
    || qspec instanceof RegExp
    || qspec instanceof Date
  ) {
    f = qspec
  } else if (d === 0 && type === 'string') {
    // return field value from query like this,
    // ```
    // row('fieldname')
    // ```
    // seems okay now, may require deeper lookup later
    f = rows && rows[0] ? rows[0][qspec] : qspec
  } else if (chainIs(qspec)) {
    // why re-use existing reql.rows, eg `spec.rows || rows`?
    // ```
    // q.expr([{ type: 'boot' }]).contains(row => r
    //  .expr([ 'cleat' ]).contains(row.getField('type')))
    //  .run();
    // ```
    // `{ type: 'boot' }` is correct, existing row at `row.getField('type')`,
    // but '.contains( ... )' would define incorrect row 'cleat'.
    //
    // current unit-tests pass, but logic could be wrong in some situations
    f = await gaqlrunChain(gst, qst, qspec, gaqlrun, rows)
  } else if (Array.isArray(qspec)) {
    // detach if spec is has args
    if (targetArgsIs(qspec.slice(-1)[0])) {
      f = await qspec.slice(-1)[0].run()
    } else {
      f = await runShallow(gst, qst, qspec, rows, d + 1, gaqlrun)
      f = targetArgsIs(f[0]) ? targetArgsParse(f[0]) : f
    }
    // render nested query objects, shallow. ex `row('id')`,
    // ```
    // q.expr([{ id: 1 }, { id: 2 }])
    //  .merge(row => ({ oldid: row('id'), id: 0 }))
    //  .run()
    // ```
  } else if (chainIsShallow(qspec)) {
    f = await runShallow(gst, qst, qspec, rows, d + 1, gaqlrun)
  } else if (type === 'function' && qspec[TYPE] === LAZY) {
    // LAZY spec-evaulations are available for "run" environments
    // that include compilation behaviour. Allows subsequent instructions
    // to be dynamically-generated based on current runtime details
    f = await qspec.run(gst, qst, rows, d + 1, gaqlrun)
  } else {
    f = qspec
  }

  return f
}

// avoid browser 'too much recursion' error; use queueMicrotask callback
const gaqlrun = async (
  gst, qst, qspec, rows, d = 0, type = typeof qspec, f = null
) => {
  return new Promise((resolve, error) => {
    queueMicrotask(() => {
      // try adding await if thie fails in browser
      runcb(gst, qst, qspec, rows, d, type, f)
        .then(resolve)
        .catch(error)
    })
  })
}

export {
  gaqlrun as default,
  gaqlrun,
  gaqlrunChain
}
